// @flow
import * as React from 'react';
import PropTypes from 'prop-types';
import idx from 'idx';
import GapItems from './GapItems';
import MessageItems from './MessageItems';
import StreamHeader from './StreamHeader';
import SystemMessageItems from './SystemMessageItems';
import ErrorReporter from 'app/components/ErrorReporter';
import StreamScrollableArea from './StreamScrollableArea';
import SequenceItemsChunker from './SequenceItemsChunker';
import PhoneCallMessageItems from './PhoneCallMessageItems';
import StreamLoadingIndicator from './StreamLoadingIndicator';
import MessageListTimeItem from 'chat/components/MessageListTimeItem';
import CenteredContainer from 'components/CenteredContainer';

import type {
  GetChatStreamItems_chatStreams,
  GetChatStreamItems_chatStreams_sequenceItems,
} from 'chat/components/ChatStream/queries/__generated__/GetChatStreamItems';
import type {
  GetAnnouncementStreamItems_entityStreams,
  GetAnnouncementStreamItems_entityStreams_sequenceItems,
} from 'chat/components/AnnouncementStream/queries/__generated__/GetAnnouncementStreamItems';
import type { ApolloError } from 'apollo-server-errors';

type Props = {
  uuid: string,
  stream: GetChatStreamItems_chatStreams | GetAnnouncementStreamItems_entityStreams | null,
  loading: boolean,
  loadGaps:
    | ((gaps: Array<GetAnnouncementStreamItems_entityStreams_sequenceItems>) => void)
    | ((gaps: Array<GetChatStreamItems_chatStreams_sequenceItems>) => void),
  error?: ApolloError,
  onScrollToBottom?: () => void,
};

type State = {
  error?: Error,
};

class Stream extends React.Component<Props, State> {
  static contextTypes = {
    entityType: PropTypes.string,
  };

  state: State = {};

  componentWillUpdate(nextProps: Props) {
    if (!this.state.error && nextProps.error) {
      this.setState({ error: nextProps.error });
    }
  }

  componentDidCatch(error: Error) {
    this.setState({ error });
  }

  scrollarea: StreamScrollableArea;

  scrollToBottom = () => {
    if (this.scrollarea) {
      this.scrollarea.scrollToBottom();
    }
  };

  render() {
    const { uuid, stream, loading, loadGaps } = this.props;
    const { error } = this.state;
    const isDistrictContext = this.context.entityType === 'district';

    if (error) {
      return (
        <StreamScrollableArea>
          <CenteredContainer>
            {/* Add DWM to the error if the stream is in the context of the district
              and it is either an EntityStream or a ChatStream with an entity (which
              does not count as a membership, so chats w/an entity always have a
              membershipsCount of 1) */}
            <ErrorReporter
              error={
                isDistrictContext &&
                stream &&
                (stream.streamType === 'entity' ||
                  (stream.__typename === 'ChatStream' && stream.membershipsCount < 2))
                  ? `DWM - ${error.toString()}`
                  : error
              }>
              <div>Oops, an error happened ({error.toString()})</div>
              <div>
                The Remind team has been alerted and is working on the issue. Please try refreshing
                the page. If the problem persists, please contact Remind support at
                support@remindhq.com.
              </div>
            </ErrorReporter>
          </CenteredContainer>
        </StreamScrollableArea>
      );
    }

    const hasItems = idx(stream, _ => _.sequenceItems.length);

    if (!stream || (loading && !hasItems)) {
      return (
        <StreamScrollableArea key="loadingStream">
          <StreamLoadingIndicator />
        </StreamScrollableArea>
      );
    }

    const firstSeqItem = stream.sequenceItems[stream.sequenceItems.length - 1];
    const firstItemIsGapItem = firstSeqItem && firstSeqItem.item.__typename === 'GapItem';

    return (
      // need the key to differentiate between the ScrollableArea when
      // the stream is loading and when it's loaded to make sure that
      // onScrollToBottom is not undefined when ScrollableArea mounts
      <StreamScrollableArea
        key="loadedStream"
        ref={(e: any) => (this.scrollarea = e)}
        onScrollToBottom={this.props.onScrollToBottom}>
        <div
          style={{ padding: '0px 18px 0px 0px' }}
          data-test-selector={`stream-${this.props.uuid}`}>
          {firstItemIsGapItem ? <StreamLoadingIndicator /> : <StreamHeader stream={stream} />}
          {stream.sequenceItems.length > 0 ? (
            <SequenceItemsChunker items={stream.sequenceItems}>
              <SequenceItemsChunker.DateChunk>
                {c => <MessageListTimeItem key={c.key()} date={c.date} />}
              </SequenceItemsChunker.DateChunk>

              <SequenceItemsChunker.GapChunk>
                {c => <GapItems key={c.key()} items={c.items} scrolledIntoView={loadGaps} />}
              </SequenceItemsChunker.GapChunk>

              <SequenceItemsChunker.MessageChunk>
                {c => <MessageItems key={c.key()} uuid={uuid} items={c.items} />}
              </SequenceItemsChunker.MessageChunk>

              <SequenceItemsChunker.SystemMessageChunk>
                {c => <SystemMessageItems key={c.key()} items={c.items} />}
              </SequenceItemsChunker.SystemMessageChunk>

              <SequenceItemsChunker.PhoneCallChunk>
                {c => <PhoneCallMessageItems key={c.key()} items={c.items} />}
              </SequenceItemsChunker.PhoneCallChunk>
            </SequenceItemsChunker>
          ) : null}
        </div>
      </StreamScrollableArea>
    );
  }
}

export default Stream;
